import React, { useRef, useState, useEffect } from 'react';
import { Modal, Button, Tag, Row, Col, Divider, Space, message } from 'antd';
import { PrinterOutlined, FilePdfOutlined, HomeOutlined, PhoneOutlined, MailOutlined } from '@ant-design/icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import storageService from '../../services/storageService';
import { formatCurrency, formatConsumption } from '../../utils/formatters';
import { amountToWords } from '../../utils/numberToWords';
import { BILLING_STATUS_LABELS } from '../../models/types';

const InvoiceDetail = ({ visible, onClose, invoice, subscriber, meter }) => {
  const invoiceRef = useRef();
  const [taxRate, setTaxRate] = useState(5);
  const [paymentDueDays, setPaymentDueDays] = useState(30);
  const [orgInfo, setOrgInfo] = useState({ address: '', phone: '', email: '' });
  const [appName, setAppName] = useState('');
  const [tariffPrice, setTariffPrice] = useState(null);

  // Load settings and tariff price from Supabase
  useEffect(() => {
    const loadSettings = async () => {
      const tax = await storageService.getSetting('tax_rate', 5);
      const dueDays = await storageService.getSetting('payment_due_days', 30);
      const address = await storageService.getSetting('app_address', '');
      const phone = await storageService.getSetting('app_phone', '');
      const email = await storageService.getSetting('app_email', '');
      const name = await storageService.getSetting('app_name', '');

      setTaxRate(tax);
      setPaymentDueDays(dueDays);
      setOrgInfo({ address, phone, email });
      setAppName(name);

      // Load tariff price based on subscriber type
      if (subscriber) {
        const tariffs = await storageService.getTariffs();
        const tariff = tariffs.find(t => t.subscriberType === subscriber.type);
        if (tariff) {
          setTariffPrice(tariff.pricePerM3);
        }
      }
    };
    loadSettings();
  }, [subscriber]);

  if (!invoice) return null;

  // Récupérer le taux de taxe
  // Toujours afficher le taux stocké dans la facture si présent (pour éviter l'effet de la config sur l'historique)
  const getTaxRateValue = () => {
    if (typeof invoice.taxRate !== 'undefined' && invoice.taxRate !== null) {
      return invoice.taxRate;
    }
    return taxRate;
  };

  // Récupérer le taux de pénalité stocké dans la facture
  const getPenaltyRateValue = () => {
    if (typeof invoice.penaltyRate !== 'undefined' && invoice.penaltyRate !== null) {
      return invoice.penaltyRate;
    }
    // fallback: configuration globale (à ajouter si besoin)
    return null;
  };

  // Récupérer le délai d'application des pénalités stocké dans la facture
  const getLateFeeThresholdValue = () => {
    if (typeof invoice.lateFeeThreshold !== 'undefined' && invoice.lateFeeThreshold !== null) {
      return invoice.lateFeeThreshold;
    }
    // fallback: configuration globale (à ajouter si besoin)
    return null;
  };

  // Récupérer le délai de paiement en jours stocké dans la facture
  const getPaymentDueDaysValue = () => {
    if (typeof invoice.paymentDueDays !== 'undefined' && invoice.paymentDueDays !== null) {
      return invoice.paymentDueDays;
    }
    return paymentDueDays;
  };

  // Récupérer les infos de l'organisation
  const getOrgInfoValue = () => {
    return orgInfo;
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'issued':
      case 'partially_paid':
        return 'orange';
      case 'paid':
        return 'green';
      case 'overdue':
        return 'red';
      case 'draft':
        return 'blue';
      case 'cancelled':
        return 'gray';
      default:
        return 'default';
    }
  };

  const handlePrintPDF = async () => {
    try {
      const element = invoiceRef.current;
      if (!element) {
        message.error('Impossible de générer le PDF');
        return;
      }

      // Capture le contenu HTML
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false
      });

      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4'
      });

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 20;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      let heightLeft = imgHeight;
      let position = 10;

      pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight + 10;
        pdf.addPage();
        pdf.addImage(imgData, 'PNG', 10, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }

      pdf.save(`Facture_${invoice.invoiceNumber}.pdf`);
      message.success('PDF téléchargé');
    } catch (error) {
      console.error(error);
      message.error('Erreur lors de la génération du PDF');
    }
  };

  const handlePrint = () => {
    const printWindow = window.open('', '', 'width=900,height=600');
    const element = invoiceRef.current;
    
    if (!element) {
      message.error('Impossible d\'imprimer');
      return;
    }

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Facture ${invoice.invoiceNumber}</title>
          <style>
            body { font-family: Arial, sans-serif; margin: 20px; }
            .invoice-container { max-width: 800px; margin: 0 auto; }
            table { width: 100%; border-collapse: collapse; margin: 20px 0; }
            th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
            th { background-color: #f5f5f5; font-weight: bold; }
            .header { text-align: center; margin-bottom: 30px; }
            .header h1 { margin: 0; }
            .section { margin: 20px 0; }
            .section-title { font-weight: bold; font-size: 14px; margin-top: 20px; margin-bottom: 10px; }
            .row { display: flex; justify-content: space-between; margin: 10px 0; }
            .amount { text-align: right; }
            .total-row { font-weight: bold; background-color: #f9f9f9; }
            @media print { body { margin: 0; } }
          </style>
        </head>
        <body>
          ${element.innerHTML}
        </body>
      </html>
    `);
    
    printWindow.document.close();
    printWindow.print();
  };

  return (
    <Modal
      title={`Détails de la facture - ${invoice.invoiceNumber}`}
      open={visible}
      onCancel={onClose}
      width={900}
      footer={[
        <Button key="close" onClick={onClose}>
          Fermer
        </Button>,
        <Button 
          key="print" 
          icon={<PrinterOutlined />}
          onClick={handlePrint}
        >
          Imprimer
        </Button>,
        <Button 
          key="pdf" 
          type="primary"
          icon={<FilePdfOutlined />}
          onClick={handlePrintPDF}
        >
          Télécharger PDF
        </Button>
      ]}
    >
      <div ref={invoiceRef} style={{ padding: '12px', backgroundColor: '#fff' }}>
        {/* En-tête */}
        <div style={{ textAlign: 'center', marginBottom: '12px', borderBottom: '2px solid #1890ff', paddingBottom: '8px' }}>
          <h1 style={{ margin: '5px 0', fontSize: '24px' }}>FACTURE D'EAU</h1>
          {appName && (
            <div style={{ fontSize: '15px', color: '#333', fontWeight: 'bold', margin: '2px 0' }}>{appName}</div>
          )}
          <p style={{ margin: '2px 0', fontSize: '14px', fontWeight: 'bold' }}>
            N° {invoice.invoiceNumber}
          </p>
        </div>
        {/* Pied de page : nom de l'application uniquement */}
        <Divider style={{ margin: '12px 0' }} />

        {/* Informations abonné et compteur */}
        <Row gutter={15} style={{ marginBottom: '12px' }}>
          <Col span={12}>
            <div style={{ marginBottom: '8px' }}>
              <h3 style={{ marginBottom: '5px', borderBottom: '1px solid #ddd', paddingBottom: '2px', fontSize: '13px' }}>
                Abonné
              </h3>
              <p style={{ margin: '2px 0', fontSize: '12px' }}>
                <strong>{invoice.subscriberName}</strong>
              </p>
              {subscriber && (
                <>
                  <p style={{ margin: '2px 0', fontSize: '11px' }}>
                    Adresse: {subscriber.address ? `${subscriber.address}, ${subscriber.city}` : 'N/A'}
                  </p>
                  <p style={{ margin: '2px 0', fontSize: '11px' }}>
                    Téléphone: {subscriber.phone || 'N/A'}
                  </p>
                  <p style={{ margin: '2px 0', fontSize: '11px' }}>
                    Type: {subscriber.type === 'individual' ? 'Particulier' : subscriber.type === 'professional' ? 'Professionnel' : 'Administration'}
                  </p>
                </>
              )}
            </div>
          </Col>
          <Col span={12}>
            <div style={{ marginBottom: '8px' }}>
              <h3 style={{ marginBottom: '5px', borderBottom: '1px solid #ddd', paddingBottom: '2px', fontSize: '13px' }}>
                Compteur
              </h3>
              {meter && (
                <>
                  <p style={{ margin: '2px 0', fontSize: '11px' }}>
                    <strong>N° Compteur:</strong> {meter.serialNumber}
                  </p>
                  <p style={{ margin: '2px 0', fontSize: '11px' }}>
                    <strong>Marque/Modèle:</strong> {meter.brand} {meter.model}
                  </p>
                  <p style={{ margin: '2px 0', fontSize: '11px' }}>
                    <strong>Statut:</strong> {meter.status === 'active' ? '✓ Actif' : meter.status}
                  </p>
                </>
              )}
            </div>
          </Col>
        </Row>

        <Divider style={{ margin: '8px 0' }} />

        {/* Détails de la facturation */}
        <div style={{ marginBottom: '12px' }}>
          <h3 style={{ marginBottom: '6px', borderBottom: '1px solid #ddd', paddingBottom: '3px', fontSize: '13px' }}>
            Détails de la facturation
          </h3>
          <Row gutter={10} style={{ marginBottom: '8px' }}>
            <Col span={12}>
              <p style={{ margin: '2px 0', fontSize: '11px' }}>
                <strong>Période de facturation:</strong> {invoice.billingPeriod}
              </p>
            </Col>
            <Col span={12}>
              <p style={{ margin: '2px 0', fontSize: '11px' }}>
                <strong>Date de facturation:</strong> {new Date(invoice.billingDate).toLocaleDateString('fr-FR')}
              </p>
            </Col>
          </Row>
          <Row gutter={10} style={{ marginBottom: '8px' }}>
            <Col span={12}>
              <p style={{ margin: '2px 0', fontSize: '11px' }}>
                <strong>Date d'échéance:</strong> {new Date(invoice.dueDate).toLocaleDateString('fr-FR')}
              </p>
              <p style={{ margin: '2px 0', fontSize: '10px', color: '#666' }}>
                Délai de paiement: {getPaymentDueDaysValue()} jours
              </p>
            </Col>
            <Col span={12}>
              <p style={{ margin: '2px 0', fontSize: '11px' }}>
                <strong>Statut:</strong> <Tag color={getStatusColor(invoice.status)}>{BILLING_STATUS_LABELS[invoice.status]}</Tag>
              </p>
            </Col>
          </Row>
        </div>

        <Divider style={{ margin: '8px 0' }} />

        {/* Consommation */}
        <div style={{ marginBottom: '12px' }}>
          <h3 style={{ marginBottom: '6px', borderBottom: '1px solid #ddd', paddingBottom: '3px', fontSize: '13px' }}>
            Consommation
          </h3>
          <Row gutter={10}>
            <Col span={6}>
              <p style={{ margin: '2px 0', fontSize: '10px' }}>
                <strong>Lecture précédente:</strong>
              </p>
              <p style={{ fontSize: '13px', fontWeight: 'bold', margin: '2px 0' }}>
                {invoice.readingStart} m³
              </p>
            </Col>
            <Col span={6}>
              <p style={{ margin: '2px 0', fontSize: '10px' }}>
                <strong>Lecture actuelle:</strong>
              </p>
              <p style={{ fontSize: '13px', fontWeight: 'bold', margin: '2px 0' }}>
                {invoice.readingEnd} m³
              </p>
            </Col>
            <Col span={6}>
              <p style={{ margin: '2px 0', fontSize: '10px' }}>
                <strong>Consommation:</strong>
              </p>
              <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#1890ff', margin: '2px 0' }}>
                {formatConsumption(invoice.totalConsumption)}
              </p>
            </Col>
            <Col span={6}>
              <p style={{ margin: '2px 0', fontSize: '10px' }}>
                <strong>Prix du m³:</strong>
              </p>
              <p style={{ fontSize: '13px', fontWeight: 'bold', color: '#52c41a', margin: '2px 0' }}>
                {tariffPrice ? `${tariffPrice.toFixed(2)} FCFA` : 'N/A'}
              </p>
            </Col>
          </Row>
        </div>

        <Divider style={{ margin: '8px 0' }} />

        {/* Calcul du montant */}
        <div style={{ marginBottom: '10px', backgroundColor: '#f9f9f9', padding: '8px', borderRadius: '4px' }}>
          <h3 style={{ marginBottom: '6px', fontSize: '13px' }}>Calcul du montant</h3>
          {tariffPrice && (
            <>
              <Row style={{ marginBottom: '5px', backgroundColor: '#e6f7ff', padding: '4px', borderRadius: '3px', fontSize: '11px' }}>
                <Col span={16}>
                  <strong>Tarif appliqué:</strong> {tariffPrice ? tariffPrice.toFixed(2) : 'N/A'} FCFA/m³
                </Col>
              </Row>
            </>
          )}
          <Row style={{ marginBottom: '4px', fontSize: '11px' }}>
            <Col span={16}>Montant de base:</Col>
            <Col span={8} style={{ textAlign: 'right' }}>
              <strong>{formatCurrency(invoice.baseAmount)}</strong>
            </Col>
          </Row>
          {invoice.tax > 0 && (
            <Row style={{ marginBottom: '4px', fontSize: '11px' }}>
              <Col span={16}>Taxes ({getTaxRateValue() ? getTaxRateValue().toFixed(1) : '0'}%):</Col>
              <Col span={8} style={{ textAlign: 'right' }}>
                <strong>{formatCurrency(invoice.tax)}</strong>
              </Col>
            </Row>
          )}
          {invoice.penalties > 0 && (
            <Row style={{ marginBottom: '4px', fontSize: '11px' }}>
              <Col span={16}>Pénalités de retard:</Col>
              <Col span={8} style={{ textAlign: 'right' }}>
                <strong style={{ color: 'red' }}>{formatCurrency(invoice.penalties)}</strong>
              </Col>
            </Row>
          )}
          <Divider style={{ margin: '6px 0' }} />
          <Row style={{ marginBottom: '6px', fontSize: '13px' }}>
            <Col span={16}>
              <strong>MONTANT TOTAL À PAYER:</strong>
            </Col>
            <Col span={8} style={{ textAlign: 'right' }}>
              <strong style={{ fontSize: '16px', color: '#1890ff' }}>
                {formatCurrency(invoice.totalAmount)}
              </strong>
            </Col>
          </Row>
        </div>

        {/* Montant en lettres */}
        <div style={{ marginBottom: '10px', backgroundColor: '#fff7e6', padding: '8px', borderRadius: '4px', borderLeft: '4px solid #faad14' }}>
          <p style={{ margin: '0', fontSize: '11px', lineHeight: '1.4' }}>
            <strong>Arrêté la présente facture à la somme de :</strong>
          </p>
          <p style={{ margin: '4px 0 0 0', fontSize: '12px', fontWeight: 'bold', color: '#d46b08', textTransform: 'uppercase' }}>
            {amountToWords(invoice.totalAmount)} FRANCS CFA
          </p>
        </div>

        {/* Paiements */}
        {invoice.paidAmount > 0 && (
          <>
            <Divider style={{ margin: '8px 0' }} />
            <div style={{ marginBottom: '10px', backgroundColor: '#f0f9ff', padding: '8px', borderRadius: '4px' }}>
              <h3 style={{ marginBottom: '6px', fontSize: '13px' }}>Situation de paiement</h3>
              <Row style={{ marginBottom: '4px', fontSize: '11px' }}>
                <Col span={16}>Montant payé:</Col>
                <Col span={8} style={{ textAlign: 'right' }}>
                  <strong style={{ color: 'green' }}>{formatCurrency(invoice.paidAmount)}</strong>
                </Col>
              </Row>
              <Row style={{ marginBottom: '4px', fontSize: '11px' }}>
                <Col span={16}>Reste à payer:</Col>
                <Col span={8} style={{ textAlign: 'right' }}>
                  <strong style={{ color: invoice.remainingAmount > 0 ? 'red' : 'green' }}>
                    {formatCurrency(invoice.remainingAmount)}
                  </strong>
                </Col>
              </Row>
            </div>
          </>

        )}

        {/* Pied de page : adresse, contact, mail avec icônes */}
        <Divider style={{ margin: '16px 0 8px 0' }} />
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap', gap: '24px', fontSize: '13px', color: '#555' }}>
          {orgInfo.address && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <HomeOutlined style={{ color: '#1890ff' }} />
              <span>{orgInfo.address}</span>
            </span>
          )}
          {orgInfo.phone && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <PhoneOutlined style={{ color: '#52c41a' }} />
              <span>{orgInfo.phone}</span>
            </span>
          )}
          {orgInfo.email && (
            <span style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <MailOutlined style={{ color: '#faad14' }} />
              <span>{orgInfo.email}</span>
            </span>
          )}
        </div>

        {/* Notes */}
        {invoice.notes && (
          <>
            <Divider style={{ margin: '8px 0' }} />
            <div>
              <h3 style={{ marginBottom: '6px', fontSize: '13px' }}>Notes</h3>
              <p style={{ backgroundColor: '#fffbe6', padding: '6px', borderRadius: '4px', margin: 0, fontSize: '11px' }}>
                {invoice.notes}
              </p>
            </div>
          </>
        )}

        
      </div>
    </Modal>
  );
};

export default InvoiceDetail;
